import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Workflow, Brain, Zap, Shield, Settings, Play, Database, Users } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function IFTTTLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Connect IFTTT to Memara
Visit IFTTT.com and search for Memara

# 2. Create your first applet
IF (trigger) THEN (create Memara memory)

# 3. Configure automation rules
Set up triggers for automatic memory capture

# 4. Start automated memory creation
Your applets will create memories automatically`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=dQw4w9WgXcQ', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="ifttt"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-black shadow" aria-label="IFTTT Logo">
          <Icon icon="simple-icons:ifttt" width={48} height={48} color="white" />
        </span>
      }
      title="IFTTT Integration"
      description="Smart automation memory for IoT and business processes. Connect any trigger to create intelligent memories automatically - from weather updates and social media posts to IoT device events and business workflow automation with persistent context understanding."
      features={[
        { icon: <Workflow className="h-4 w-4 text-gray-900" />, title: 'Intelligent Automation Workflows', description: 'Create sophisticated memory capture workflows with IFTTT applets and smart trigger recognition for business processes.' },
        { icon: <Brain className="h-4 w-4 text-gray-900" />, title: 'AI-Powered Trigger Intelligence', description: 'Advanced trigger recognition with AI-powered context understanding for relevant memory creation and business intelligence.' },
        { icon: <Zap className="h-4 w-4 text-gray-900" />, title: 'Instant IoT Memory Automation', description: 'Real-time memory creation from IoT devices, smart home events, and connected business systems with sub-second processing.' },
        { icon: <Shield className="h-4 w-4 text-gray-900" />, title: 'Enterprise-Grade Integration Security', description: 'Secure connection to IFTTT platform with multi-tenant isolation and enterprise data protection standards.' },
        { icon: <Database className="h-4 w-4 text-gray-900" />, title: 'Cross-Platform Data Intelligence', description: 'Unified memory capture across 600+ IFTTT services with intelligent data mapping and context preservation.' },
        { icon: <Users className="h-4 w-4 text-gray-900" />, title: 'Team Automation Memory', description: 'Shared organizational memory from automated workflows with collaborative intelligence and role-based access controls.' },
      ]}
      setupSteps={[
        'Connect IFTTT platform to Memara API endpoint',
        'Create intelligent automation applets with memory actions',
        'Configure smart automation rules and trigger intelligence',
        'Start automated memory creation from all connected services',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-gray-300 text-gray-900 hover:bg-gray-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-gray-300 text-gray-900 hover:bg-gray-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Settings className="w-4 h-4 mr-2" />,
        },
      ]}
      videoUrl="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
      microcopy="Trusted by automation engineers, IoT specialists, and enterprise teams using IFTTT for smart business process automation and connected device intelligence."
      trustedBy="IFTTT, Enterprise Automation Teams, and more."
      testimonial="Memara's IFTTT integration transforms our smart automation workflows into an intelligent memory system that captures context from every connected device and business process, creating unprecedented organizational intelligence."
    />
  );
}